<script>
  import { getTheme } from "../utilities/themes";

  // @ts-check

  import Grid from "./Grid.svelte";

  /**
  Primarily for testing
  @type {string} */
  export let id = undefined;

  /**
    Number of columns to be divided
    Mobile accepts only 1, 2 ,4 (because mobile Grid only has 4 columns)
    And desktop accepts 1, 2, 3, 4, 6, 12
  @type { 1 | 2 | 3 | 4 | 6 | 12} */
  export let numberOfColumns;

  /**
  Total gridColumns for mobile, tablet and desktop
  @type {import('./Grid.svelte').GRID_COLUMNS} */
  export let subGridColumns;

  $: colSpanMobile = Math.floor((subGridColumns?.mobile || 4) / numberOfColumns);
  $: colSpanTablet = Math.floor((subGridColumns?.tablet || 12) / numberOfColumns);
  $: colSpanDesktop = Math.floor((subGridColumns?.desktop || 12) / numberOfColumns);

  /**
  Used mainly for breakpoint support like `hidden md:grid`
  @type {string} */
  export let containerClasses = "";

  /** 
  Class to be applied to the Grid
  @type {string} */
  export let gridClass = "";

  /**
   * The theme of the component using the Rule.
   * @type {import('../utilities/themes').ThemeName}
   */
  export let theme = "white";

  $: themeClass = getTheme(theme)?.ruleColor || "bg-black";
</script>

<!--
@component
To have a Grid display visible column dividers,
this needs to be placed within a relative wrapper so it get's the correct sizing
in case of multiple breakpoints, use multiple dividers
- Usage:
  ```svelte
  <GridContainer gridInnerClasses="relative"> // In the case of the container it needs to be the innerClass because it has the max-width (or you can just use a nested div with position: relative)
    <GridDivider numberOfColumns={3} containerClasses="hidden lg:block" />
    <GridDivider numberOfColumns={2} containerClasses="block lg:hidden" />
    <Grid>
      [...]
    </Grid>
  </GridContainer>
  ```
-->
<div class="pointer-events-none absolute inset-0 h-full w-full p-[inherit] {containerClasses}">
  <Grid {id} gridClass="h-full w-full {gridClass}" {subGridColumns}>
    {#each Array(numberOfColumns).fill(numberOfColumns) as _, index}
      {@const hiddenInMobile = index > 4}
      <div
        style="--col-span-mobile: {colSpanMobile}; --col-span-tablet: {colSpanTablet}; --col-span-desktop: {colSpanDesktop}"
        class="column-span relative after:absolute after:-right-grid-gutter-half after:top-0 after:block after:h-full after:w-px last-of-type:after:content-none
      {hiddenInMobile ? 'hidden md:block' : 'md:block'} after:{themeClass}
      "
      />
    {/each}
  </Grid>
</div>

<style>
  .column-span {
    grid-column: span var(--col-span-mobile);
  }

  @media (min-width: 680px) {
    .column-span {
      grid-column: span var(--col-span-tablet);
    }
  }

  @media (min-width: 1200px) {
    .column-span {
      grid-column: span var(--col-span-desktop);
    }
  }
</style>
